import React, { useCallback, useEffect, useState } from "react"
import { _single, _SSTNode } from "../../../core/services/dataBean/EntityCatalog"
import useDataBean from "../../../core/services/dataBean/useDataBean"
import { BasicPage } from "../../theme/Component/BasicPage"
import { SelectPeriodoPhva } from "./SelectPeriodoPhva"
import { BasicButton } from "../../theme/Component/BasicButton"
import useModal, { MODAL_md, MODAL_XL } from "../../theme/Component/hooks/useModal"
import { Modal } from "../../theme/Component/Modal"
import { FileInput } from "../../theme/Component/FileInput"
import { toast } from "react-toastify"
import "./Phva.css"
import { FileService } from "../../../core/services/FileService"
import { VisorAnyDocument } from "../../documents/VisorAnyDocument"

type PHVANode = {
    IDSSTNode: number
    IDParent?: number | null
    IDSSTDocument?: number | null
    JsonCode?: string | null
    Name?: string | null
    TypeNode?: number | null
    Phva?: string | null
    LevelNode?: number | null
    OrderIndex?: number | null
    PathNode?: string | null
    FileName?: string | null
    Media?: string | null
    MediaContext?: string | null
    ChildCount?: number | null
    children?: PHVANode[]
    expanded?: boolean
    loading?: boolean
    loaded?: boolean
}

interface componentProps {
    isAdmin?: boolean;
}

const TYPE_FOLDER = 1
const TYPE_DOCUMENT = 2

export const Phva = ({ isAdmin }: componentProps) => {
    const _sst = useDataBean(_SSTNode)
    const { lista, setLista, instance, bean, setBean, handleChange } = _sst

    const [periodo, setPeriodo] = useState<number>(0)
    const [periodo2, setPeriodo2] = useState<number>(0)
    const [error, setError] = useState("")
    const [loadingRoot, setLoadingRoot] = useState(false)

    const [selectedNode, setSelectedNode] = useState<PHVANode | null>(null)
    const [folderName, setFolderName] = useState("")
    const [file, setFile] = useState<any>({})

    const modalimportar = useModal("Importar", MODAL_md)
    const modalAddFile = useModal("Agregar Documento", MODAL_md)
    const modalAddFolder = useModal("Agregar Directorio", MODAL_md)
    const modalDeleteNode = useModal("Eliminar", MODAL_md);


    const normalizeNode = useCallback((item: any): PHVANode => {
        const p = item?.DataBeanProperties || item

        return {
            IDSSTNode: Number(p.IDSSTNode),
            IDParent: p.IDParent != null ? Number(p.IDParent) : null,
            IDSSTDocument: p.IDSSTDocument != null ? Number(p.IDSSTDocument) : null,
            JsonCode: p.JsonCode ?? null,
            Name: p.Name ?? "",
            TypeNode: p.TypeNode != null ? Number(p.TypeNode) : TYPE_FOLDER,
            Phva: p.Phva ?? null,
            LevelNode: p.LevelNode != null ? Number(p.LevelNode) : null,
            OrderIndex: p.OrderIndex != null ? Number(p.OrderIndex) : null,
            PathNode: p.PathNode ?? null,
            FileName: p.FileName ?? null,
            Media: p.Media ?? null,
            MediaContext: p.MediaContext ?? null,
            ChildCount: p.ChildCount != null ? Number(p.ChildCount) : 0,
            children: [],
            expanded: false,
            loading: false,
            loaded: false
        }
    }, [])

    const extractList = useCallback((resp: any) => {
        if (Array.isArray(resp)) return resp
        return resp?.DataBeanProperties?.ObjectValue || resp?.data || resp?.list || []
    }, [])

    const getPHVARoot = useCallback(() => {
        if (!periodo) {
            setLista([])
            return
        }

        setLoadingRoot(true)
        setError("")

        instance.getPHVARoot(periodo).then(
            (resp: any) => {
                const data = extractList(resp)
                const rootNodes = (data || []).map((item: any) => normalizeNode(item))
                setLista(rootNodes)
                setLoadingRoot(false)
            },
            (err: any) => {
                setError(err?.message || "No fue posible cargar la raíz del PHVA")
                setLoadingRoot(false)
            }
        )
    }, [extractList, instance, normalizeNode, periodo, setLista])

    const getPHVAChildren = useCallback((idParent: number) => {
        return instance.getPHVAChildren(idParent).then((resp: any) => {
            const data = extractList(resp)
            return (data || []).map((item: any) => normalizeNode(item))
        })
    }, [extractList, instance, normalizeNode])

    const updateNodeInTree = useCallback((nodes: PHVANode[], nodeId: number, updater: (node: PHVANode) => PHVANode): PHVANode[] => {
        return (nodes || []).map((node) => {
            if (node.IDSSTNode === nodeId) {
                return updater(node)
            }

            if (node.children && node.children.length > 0) {
                return {
                    ...node,
                    children: updateNodeInTree(node.children, nodeId, updater)
                }
            }

            return node
        })
    }, [])

    const refreshNodeChildren = useCallback(async (parentNode: PHVANode | null) => {
        if (!parentNode) {
            getPHVARoot()
            return
        }

        try {
            const children = await getPHVAChildren(parentNode.IDSSTNode)

            setLista((prev: PHVANode[]) =>
                updateNodeInTree(prev || [], parentNode.IDSSTNode, (current) => ({
                    ...current,
                    children,
                    expanded: true,
                    loaded: true,
                    loading: false,
                    ChildCount: children.length
                }))
            )
        } catch (err: any) {
            setError(err?.message || "No fue posible refrescar el nodo")
        }
    }, [getPHVAChildren, getPHVARoot, setLista, updateNodeInTree])

    const toggleNode = useCallback(async (node: PHVANode) => {
        const isFolder = Number(node.TypeNode) === TYPE_FOLDER
        const hasChildren = Number(node.ChildCount || 0) > 0

        if (!isFolder || !hasChildren) {
            return
        }

        if (node.expanded) {
            setLista((prev: PHVANode[]) =>
                updateNodeInTree(prev || [], node.IDSSTNode, (current) => ({
                    ...current,
                    expanded: false
                }))
            )
            return
        }

        if (node.loaded) {
            setLista((prev: PHVANode[]) =>
                updateNodeInTree(prev || [], node.IDSSTNode, (current) => ({
                    ...current,
                    expanded: true
                }))
            )
            return
        }

        setLista((prev: PHVANode[]) =>
            updateNodeInTree(prev || [], node.IDSSTNode, (current) => ({
                ...current,
                loading: true
            }))
        )

        try {
            const children = await getPHVAChildren(node.IDSSTNode)

            setLista((prev: PHVANode[]) =>
                updateNodeInTree(prev || [], node.IDSSTNode, (current) => ({
                    ...current,
                    children,
                    expanded: true,
                    loaded: true,
                    loading: false
                }))
            )
        } catch (err: any) {
            setLista((prev: PHVANode[]) =>
                updateNodeInTree(prev || [], node.IDSSTNode, (current) => ({
                    ...current,
                    loading: false
                }))
            )
            setError(err?.message || "No fue posible cargar los hijos del nodo")
        }
    }, [getPHVAChildren, setLista, updateNodeInTree])

    const openAddFolderModal = (node: PHVANode | null) => {
        setSelectedNode(node)
        setFolderName("")
        modalAddFolder.open()
    }

    const openDeleteModal = (node: PHVANode | null) => {
        setSelectedNode(node)
        modalDeleteNode.open(node.TypeNode == 1 ? "Eliminar Directorio" : "Eliminar Documento")
    }

    const openAddFileModal = (node: PHVANode | null) => {
        setSelectedNode(node)
        setFile({})
        setBean({
            ...bean,
            VersionDoc: 1
        })
        modalAddFile.open()
    }

    const importar = () => {
        if (periodo2 === 0) {
            toast.warning("Debe seleccionar un periodo")
            return
        }

        if (!file?.Media || !file?.MediaContext) {
            toast.warning("Debe cargar un archivo ZIP")
            return
        }

        instance.importPHVA(_single.getAccountID(), file.Media, file.MediaContext, periodo2).then(
            () => {
                toast.success("Importación realizada correctamente")
                if (periodo === periodo2) {
                    getPHVARoot()
                }
                modalimportar.close()
                setFile({})
            },
            (err: any) => {
                toast.error(err?.message || "No fue posible importar el PHVA")
            }
        )
    }

    const addFile = () => {
        if (!periodo) {
            toast.warning("Debe seleccionar un periodo")
            return
        }

        if (!selectedNode) {
            toast.warning("Debe seleccionar una carpeta padre")
            return
        }

        if (Number(selectedNode.TypeNode) !== TYPE_FOLDER) {
            toast.warning("Solo puede agregar documentos dentro de una carpeta")
            return
        }

        if (!file?.Media || !file?.MediaContext) {
            toast.warning("Debe seleccionar un archivo")
            return
        }

        const versionDoc = bean?.VersionDoc ? Number(bean.VersionDoc) : 1
        const orderIndex = Number(selectedNode.ChildCount || 0) + 1

        instance.addDocumentPHVA(
            selectedNode.IDSSTNode,
            file.Media,
            file.MediaContext,
            file.Name,
            versionDoc,
            _single.getAccountID()
        ).then(
            async () => {
                toast.success("Documento agregado correctamente")
                modalAddFile.close()
                setFile({})
                await refreshNodeChildren(selectedNode)
            },
            (err: any) => {
                toast.error(err?.message || "No fue posible agregar el documento")
            }
        )
    }

    const deleteNode = () => {
        const parentId = selectedNode?.IDParent;
        instance.deleteNodeSST(_single.getAccountID(), selectedNode.IDSSTNode).then(
            () => {
                toast.success(selectedNode.TypeNode == 1 ? "Directorio Eliminado" : "Documento Eliminado");
                modalDeleteNode.close();
                setSelectedNode(null);

                if (parentId) {
                    refreshNodeChildren({ IDSSTNode: parentId } as PHVANode);
                } else {
                    getPHVARoot();
                }
            }
        ).catch(err => toast.error(err))
    }

    const addFolder = () => {
        if (!periodo) {
            toast.warning("Debe seleccionar un periodo")
            return
        }

        if (!selectedNode) {
            toast.warning("Debe seleccionar una carpeta padre")
            return
        }

        if (Number(selectedNode.TypeNode) !== TYPE_FOLDER) {
            toast.warning("Solo puede agregar directorios dentro de una carpeta")
            return
        }

        if (!folderName || !folderName.trim()) {
            toast.warning("Debe escribir el nombre del directorio")
            return
        }

        const orderIndex = Number(selectedNode.ChildCount || 0) + 1

        instance.addFolderPHVA(
            periodo,
            selectedNode.IDSSTNode,
            folderName.trim(),
            orderIndex,
            _single.getAccountID()
        ).then(
            async () => {
                toast.success("Directorio agregado correctamente")
                modalAddFolder.close()
                setFolderName("")
                await refreshNodeChildren(selectedNode)
            },
            (err: any) => {
                toast.error(err?.message || "No fue posible agregar el directorio")
            }
        )
    }



    useEffect(() => {
        getPHVARoot()
    }, [periodo])

    return (
        <BasicPage titulo="PHVA">
            <div className="row">
                <div className="col-md-6">
                    <SelectPeriodoPhva idSelector={periodo} onChange={(e) => { setPeriodo(parseInt(e)) }} />
                </div>
                <div className="col-md-6 mb-2">
                    <BasicButton useMargin icon="" eventClick={getPHVARoot}>Buscar</BasicButton>
                </div>

                <div className="col-md-12">
                    {error && (
                        <div className="alert alert-danger alert-dismissible fade show" role="alert">
                            {error}
                            <button type="button" className="btn-close" onClick={() => setError("")}></button>
                        </div>
                    )}

                    <div className="card shadow-sm">
                        {isAdmin &&
                            <div className="card-header d-flex justify-content-between align-items-center">
                                <span className="link cursor" onClick={() => { modalimportar.open() }}>Importar</span>
                                {/* <div className="d-flex gap-2">
                                    <button className="btn btn-outline-primary btn-sm" onClick={() => openAddFolderModal(null)} >
                                        Nueva estructura
                                    </button>
                                </div> */}
                            </div>
                        }

                        <div className="card-body">
                            {loadingRoot ? (
                                <div className="text-muted">Cargando estructura...</div>
                            ) : !lista || lista.length === 0 ? (
                                <div className="text-muted">No hay información para este período.</div>
                            ) : (
                                <PHVATree
                                    nodes={lista}
                                    onToggle={toggleNode}
                                    onAddFolder={openAddFolderModal}
                                    onAddFile={openAddFileModal}
                                    onDeleteNode={openDeleteModal}
                                    _isAdmin={isAdmin}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-md-12">
                    <Modal modal={modalimportar.modal} updateModal={modalimportar.setModal} eventModal={importar} onSubmitLabel="Importar">
                        <div className="row">
                            <div className="col-md-12">
                                <SelectPeriodoPhva idSelector={periodo2} onChange={(e) => { setPeriodo2(parseInt(e)) }} />
                            </div>
                            <div className="col-md-12">
                                <FileInput directory="sst" onCreate={(e) => { setFile(e) }} />
                            </div>
                        </div>
                    </Modal>

                    <Modal modal={modalAddFile.modal} updateModal={modalAddFile.setModal} eventModal={addFile} onSubmitLabel="Agregar Documento">
                        <div className="row">
                            <div className="col-md-12 mb-2">
                                <label className="form-label">Nodo padre</label>
                                <input
                                    className="form-control"
                                    value={selectedNode ? `${selectedNode.JsonCode || ""} ${selectedNode.Name || ""}`.trim() : ""}
                                    readOnly
                                />
                            </div>

                            <div className="col-md-6">
                                <label className="form-label">Versión</label>
                                <input
                                    type="number"
                                    className="form-control"
                                    name="VersionDoc"
                                    id="VersionDoc"
                                    value={bean?.VersionDoc || 1}
                                    onChange={handleChange}
                                />
                            </div>

                            <div className="col-md-12 mt-3">
                                <FileInput directory="sst" onCreate={(e) => { setFile(e) }} />
                            </div>
                            {/* <div className="col-md-12">
                                <PrintJson json={file} />
                            </div> */}
                        </div>
                    </Modal>

                    <Modal modal={modalAddFolder.modal} updateModal={modalAddFolder.setModal} eventModal={addFolder} onSubmitLabel="Agregar Directorio">
                        <div className="row">
                            <div className="col-md-12 mb-2">
                                <label className="form-label">Nodo padre</label>
                                <input
                                    className="form-control"
                                    value={selectedNode ? `${selectedNode.JsonCode || ""} ${selectedNode.Name || ""}`.trim() : ""}
                                    readOnly
                                />
                            </div>

                            <div className="col-md-12">
                                <label className="form-label">Nombre del directorio</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={folderName}
                                    onChange={(e) => setFolderName(e.target.value)}
                                    placeholder="Ej: 1.6 Programa de auditoría"
                                />
                            </div>
                        </div>
                    </Modal>

                    <Modal modal={modalDeleteNode.modal} updateModal={modalDeleteNode.setModal} eventModal={deleteNode} onSubmitLabel="Confirmar Elimininación">
                        <p>Desea Eliminar {selectedNode?.Name} </p>
                    </Modal>
                </div>
            </div>
        </BasicPage>
    )
}

type PHVATreeProps = {
    nodes: PHVANode[]
    onToggle: (node: PHVANode) => void | Promise<void>
    onAddFolder: (node: PHVANode) => void
    onAddFile: (node: PHVANode) => void
    onDeleteNode: (node: PHVANode) => void
    _isAdmin?: boolean
}

const PHVATree = ({ nodes, onToggle, onAddFolder, onAddFile, onDeleteNode, _isAdmin }: PHVATreeProps) => {
    return (
        <div className="phva-tree">
            {(nodes || []).map((node) => (
                <PHVATreeNode
                    key={node.IDSSTNode}
                    node={node}
                    level={0}
                    onToggle={onToggle}
                    onAddFolder={onAddFolder}
                    onAddFile={onAddFile}
                    ondeleteNode={onDeleteNode}
                    isAdmin={_isAdmin}
                />
            ))}
        </div>
    )
}

type PHVATreeNodeProps = {
    node: PHVANode
    level: number
    onToggle: (node: PHVANode) => void | Promise<void>
    onAddFolder: (node: PHVANode) => void
    onAddFile: (node: PHVANode) => void
    ondeleteNode: (node: PHVANode) => void
    isAdmin?: boolean;
}

const PHVATreeNode = ({
    node,
    level,
    onToggle,
    onAddFolder,
    onAddFile,
    ondeleteNode,
    isAdmin
}: PHVATreeNodeProps) => {

    const isFolder = Number(node.TypeNode) === TYPE_FOLDER
    const isDocument = Number(node.TypeNode) === TYPE_DOCUMENT
    const hasChildren = Number(node.ChildCount || 0) > 0

    const left = level * 20

    const modalDocument = useModal("Visor Documento", MODAL_XL)
    const [document, setDocument] = useState<any>(null)
    const fileUtils = new FileService()

    const showDocument = (node: any) => {
        if (node.Media && node.MediaContext) {
            const url = fileUtils.getUrlFile(node.MediaContext, node.Media)
            node.URL = url
            setDocument(node)
            modalDocument.open()
        }
    }

    return (
        <div className="phva-node-wrapper">

            <div
                className={`phva-node ${node.expanded ? "open" : ""} ${isFolder ? "folder" : "file"}`}
                style={{ marginLeft: `${left}px` }}
                onClick={() => isFolder && hasChildren && onToggle(node)}
            >

                {/* Toggle / placeholder */}
                <div className="phva-toggle">
                    {isFolder && hasChildren ? (
                        node.loading ? (
                            <span className="spinner-border spinner-border-sm"></span>
                        ) : (
                            <i className={`ri-arrow-${node.expanded ? "down" : "right"}-s-line`} />
                        )
                    ) : (
                        <div className="phva-toggle-placeholder" />
                    )}
                </div>

                {/* Icono */}
                <div className="phva-icon">
                    {isFolder ? (
                        node.expanded
                            ? <i className="ri-folder-open-line text-warning" />
                            : <i className="ri-folder-line text-warning" />
                    ) : (
                        <i className="ri-file-line text-secondary" />
                    )}
                </div>

                {/* Contenido */}
                <div className="phva-content">
                    <div className={isFolder ? "phva-title folder" : "phva-title file"}>
                        {node.JsonCode ? `${node.JsonCode} ` : ""}
                        {node.Name}

                        {isDocument && (
                            <span
                                className="phva-link"
                                onClick={(e) => {
                                    e.stopPropagation()
                                    showDocument(node)
                                }}
                            >
                                Ver
                            </span>
                        )}
                    </div>

                    <div className="phva-meta">
                        {node.Phva && `PHVA: ${node.Phva}`}
                        {node.FileName && ` | ${node.FileName}`}
                        {hasChildren && ` | ${node.ChildCount} hijos`}
                    </div>
                </div>

                {/* Acciones */}
                {isAdmin && (
                    <div
                        className="phva-actions"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {isFolder && (
                            <>
                                <button onClick={() => onAddFolder(node)}>
                                    <i className="ri-folder-add-line"></i>
                                </button>
                                <button onClick={() => onAddFile(node)}>
                                    <i className="ri-file-add-line"></i>
                                </button>
                                <button onClick={() => ondeleteNode(node)}>
                                    <i className="ri-delete-bin-2-line"></i>
                                </button>
                            </>
                        )}

                        {isDocument && (
                            <button onClick={() => ondeleteNode(node)}>
                                <i className="ri-delete-bin-2-line"></i>
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Modal documento */}
            <Modal modal={modalDocument.modal} updateModal={modalDocument.setModal}>
                <div className="row">
                    <div className="col-md-12">
                        {document?.URL && <VisorAnyDocument url={document.URL} />}
                    </div>
                </div>
            </Modal>

            {/* Children */}
            {node.expanded && node.children?.length > 0 && (
                <div>
                    {node.children.map(child => (
                        <PHVATreeNode
                            key={child.IDSSTNode}
                            node={child}
                            level={level + 1}
                            onToggle={onToggle}
                            onAddFolder={onAddFolder}
                            onAddFile={onAddFile}
                            ondeleteNode={ondeleteNode}
                            isAdmin={isAdmin}
                        />
                    ))}
                </div>
            )}
        </div>
    )
}